import { SagaIterator } from '@redux-saga/core';
import { all, call, fork, put, select, takeEvery } from '@redux-saga/core/effects';
import { PayloadAction } from "@reduxjs/toolkit";
import { uploadData } from "aws-amplify/storage";
import { datetime, getStartOfWeek } from "../../util/library/datetime";
import { CapabilityService } from "../../util/services/capability";
import { factoryService } from "../../util/services/factory";
import {
    OpService
} from "../../util/services/operation";
import { SiteService } from "../../util/services/site";
import { selectFactory } from '../../util/store/factory/constants';
import { selectOneSetting, selectOpMode, selectSetting, selectTenantId } from "../../util/store/selectors";
import { getStore } from '../../util/store/store';
import { aDayMillisecond, aWeekMillisecond, CONTRACT_PERMISSION_TYPE, CONTRACT_TYPE, initAutoPlanConfig, OP_STATUS, OPERATION_CONFIRM_STATUS, OPERATION_TYPE, TIME_PER_STEP, VALIDATION_STATUS, VIEW_SMOOTH } from "../../util/varibles/constants";
import { cloneObj, getElById, getKeyWeek, getMondayByKey, showErrorResponse } from "../../util/varibles/global";
import {
    IContract,
    IFactory,
    ILevelByVessel,
    IOperation,
    IOperation2,
    IOperations,
    IRegion,
    IServiceTask,
    ISite,
    IVessel,
    TOperation
} from "../../util/varibles/interface";
import { notify, NotifyCode } from "../../util/varibles/message";
import { PlanSetting, PreferenceSetting } from "../../util/varibles/userSetting";
import { generateError } from './_components/ErrorBox';
import { onMouseDown } from './BiologyBrowser/constants';
import { selectPlan } from './constants';
import { planOpActions } from "./reducer";
import { addFishChanges, checkValidOperation, convertOpsFromResponse, downLevel, generate, getActivityLogDurationEst, getContract, ICheckValidOperation, IProperty, paramsActivityLog, pushOperations, removeFishChanges, updateNextOperation, upLevel } from "./util";
import { FuncOp, getTimeByOpMode } from './util/function_operation/constants';
import { focusOp, funcGlobal, resolveActivityLog, updateTime } from './util/function_operation/global';
import { funcHarvest } from './util/function_operation/harvest';

function* initialWatcher(): Generator<any, any, [IFactory[], IRegion[], { vessels: IVessel[] }, IServiceTask[]]> {
    try {
        const [factories, serviceTasks] = yield all([
            call(factoryService.gets),
            call(CapabilityService.serviceTasks)
        ])

        yield put(planOpActions.initialSuccess({
            factories,
            serviceTasks
        }));

    } catch (error) {
        console.log(error)
    }
}

const initConfirm = {
    true: {
        title: 'Saved operation',
        fetch: OpService.saveOps,
        content: NotifyCode.S11,
    },
    false: {
        title: 'Sent operation',
        fetch: OpService.createOps,
        content: NotifyCode.S12
    }
}
const Symbol1 = '_'

const getTotalWeightErrors = (
    errors: Record<string, unknown> | undefined,
    opIndexes: Record<string, IOperation>
) => Object.keys(errors || {}).reduce<any[]>((messages, errorKey) => {
    const keys = errorKey.split('.');
    if (!keys.includes('total_weight'))
        return messages;

    const op = opIndexes[[keys[1], keys[3]].join(Symbol1)];
    if (op)
        messages.push(generateError(op, Number(keys[5])));

    return messages;
}, []);

const addOp = (payload: any, currentOp: IOperation) => {
    const operation = generate(currentOp as IOperation2)
    const { vessel } = currentOp;
    const currentVesselIndex = payload.findIndex((item: any) => item.vessel_id === vessel.id);
    let index;
    if (currentVesselIndex !== -1) {
        payload[currentVesselIndex].operations.push(operation);
        index = [currentVesselIndex, payload[currentVesselIndex].operations - 1].join(Symbol1);
    } else {
        payload.push({ vessel_id: vessel.id, vessel_owner_id: vessel.tenant_id, operations: [operation] })
        index = [payload.length - 1, 0].join(Symbol1);
    }
    return { rs: payload, opIndex: index };
}

function* confirmOperationsWatcher(args: PayloadAction<{
    operations: IOperation[],
    isSave: boolean
}>): Generator<any, any, any> {
    let title = 'Operation';
    let newOps: IOperations = {};
    let confirmVessels: any;
    let opIndexes: { [id: string]: IOperation } = {}
    try {
        const { operations, isSave = false } = args.payload;
        const confirmConfig = initConfirm[`${isSave}`];
        if (!confirmConfig)
            throw new Error(`Error system`);

        const { fetch, content } = confirmConfig;
        title = confirmConfig.title;
        newOps = yield select(selectPlan.ops);

        let files = yield select(selectPlan.files);
        let level: ILevelByVessel = yield select(selectPlan.level);
        let opsDelete = yield select(selectPlan.opsDelete);
        let fishChanges = yield select(selectPlan.fishChanges);
        let delete_operations: string[] = [];
        let related: {
            [id: string]: {
                [id: string]: IOperation[]
            }
        } = {};

        confirmVessels = operations.reduce((payload: any[], currentOp: IOperation) => {
            const { id, related_id, operation_type } = currentOp.operation;
            const { [id]: item, ...args } = newOps;
            if (!item)
                return payload;

            newOps = args;
            level = downLevel(level, item, newOps);

            const { [id]: fileOfOp = [], ...subFiles } = files;
            if (fileOfOp.length > 0) {
                files = { ...subFiles };
                fileOfOp.forEach((item: any) => uploadData({ path: `public/operations/${item.key}`, data: item.file, options: { contentType: item.file.type } }).result.then())
            }

            const { [id]: deleteByOp = [], ...operationsDeleteNew } = opsDelete;
            opsDelete = operationsDeleteNew;
            delete_operations = [...delete_operations, ...deleteByOp.map((item: IOperation) => item.operation.id)]
            if (related_id) {
                const isMain = `ids_${operation_type !== OPERATION_TYPE.SUPPORT}`;
                related = {
                    ...related, [related_id]: {
                        ...related[related_id] || {},
                        [isMain]: [...related[related_id]?.[isMain] || [], item],
                    }
                }
            }

            fishChanges = removeFishChanges(fishChanges, currentOp)
            const { rs, opIndex } = addOp(payload, currentOp)
            opIndexes[opIndex] = currentOp;
            return rs
        }, []);

        const warning: ({ [id: string]: IOperation[] })[] = [];

        Object.keys(related).forEach(relatedKey => {
            let keys = related[relatedKey];
            Object.keys(newOps).forEach((key: string) => {
                if (newOps[key].operation.related_id === relatedKey) {
                    if (newOps[key].action_type) {
                        const { [key]: deleteByOp = [], ...operationsDeleteNew } = opsDelete;
                        opsDelete = operationsDeleteNew;
                        delete_operations = [...delete_operations, ...deleteByOp.map((item: IOperation) => item.operation.id)]
                        const { [key]: secondaryOp, ...subArgs } = newOps;
                        const isMain = `ids_${newOps[key].operation.operation_type !== OPERATION_TYPE.SUPPORT}`;
                        newOps = subArgs
                        level = downLevel(level, secondaryOp, newOps);
                        const { rs, opIndex } = addOp(confirmVessels, secondaryOp);
                        confirmVessels = rs;
                        opIndexes[opIndex] = secondaryOp;
                        keys = { ...keys, [isMain]: [...keys[isMain] || [], secondaryOp], }
                    } else {
                        keys = { ...keys, related: [...keys.related || [], newOps[key]] }
                    }
                }
            });
            const mainOp = keys.ids_true?.[0];
            const isAllNew = (keys.ids_false || []).every(item => item.operation.status === OP_STATUS.NEW);
            if (keys?.related?.length > 0 && (!(mainOp?.operation.status === OP_STATUS.NEW) || !isAllNew)) {
                warning.push(keys);
            }
        })

        const { new_operations, old_operations } = yield call(fetch, { vessels: confirmVessels, delete_operations });

        const vessels: IVessel[] = yield select(selectPlan.vessels);
        yield put(planOpActions.confirmOpSuccess({
            operations: [...new_operations, ...old_operations].reduce((list: IOperations, data: any) => {
                const { id, vessel_id } = data.operation;
                list[id] = { ...data, vessel: vessels.find(item => item.id === vessel_id) };
                level = upLevel(level, list[id], list);
                return list
            }, newOps),
            level,
            operationsDelete: opsDelete,
            files,
            fishChanges
        }));
        const newCode = new_operations.map((item: any) => `#${item.operation.operation_code}`);
        const oldCode = old_operations.map((item: any) => `#${item.operation.operation_code}`);
        if (warning.length > 0)
            notify.warn(NotifyCode.W4, 'Please check and update (if needed) related operations', { style: { width: '520px' } })([warning]);
        notify.success(content)([newCode, oldCode]);

    } catch (error: any) {
        yield put(planOpActions.confirmOpFailure());
        const { status } = error.response || {};
        if (status === undefined) {
            showErrorResponse(title + ' failure', error);
            return;
        }
        switch (status) {
            case 448: {
                const { overlap_ids = [] } = error.data || {};
                const ids: string[] = [];
                const codes = overlap_ids.reduce((list: string[], key: string) => {
                    const { operation_code } = (newOps[key] || { operation: {} }).operation;
                    if (operation_code) {
                        ids.push(key);
                        list.push(`#${operation_code}`);
                    }
                    return list;
                }, []);
                if (codes.length > 0)
                    notify.error(NotifyCode.E28, title + ' failure')([codes.join(', ')]);
                else
                    notify.error(NotifyCode.E27, title + ' failure')();

                if (ids.length > 0) {
                    const el = document.getElementById(ids[0]);
                    if (el)
                        el.scrollIntoView(VIEW_SMOOTH);
                }
                break;
            }
            case 450: {
                const { errors } = error.data || {};
                const responseMessage = typeof error.data?.message === 'string' ? error.data.message : '';
                if (/^Data is not reasonable\.\s*Please check and try again\.$/.test(responseMessage)) {
                    notify.error(NotifyCode.E50, title + ` failure (${status})`)()
                    return;
                }
                const message = getTotalWeightErrors(errors, opIndexes);
                if (message.length > 0) {
                    notify.errorRaw(message, title + ` failure (${status})`)
                } else if (responseMessage) {
                    notify.errorRaw(responseMessage, title + ` failure (${status})`)
                } else
                    notify.error(NotifyCode.E15, title + ` failure (${status})`)()
                break;
            }
            default: {
                const { errors } = error.data || {}
                const message = getTotalWeightErrors(errors, opIndexes);
                if (message.length > 0) {
                    notify.errorRaw(message, title + ` failure (${status})`)
                } else
                    notify.error(NotifyCode.E15, title + ` failure (${status})`)()

            }
        }
    }
}

function* lazyLoadingWatcher({ ...args }): Generator<any, any, any> {
    const { key, duplicateFinishTime, action } = args.payload;
    try {
        const oldWeeks = yield select(selectPlan.weeks);
        const beforeCount = yield select(selectPlan.beforeCount);
        const afterCount = yield select(selectPlan.afterCount);

        let isLoad,
            startTime,
            finishTime,
            newWeeks,
            result;
        if (key === 'beforeCount') {
            isLoad = beforeCount;
            const [year, weekNo]: any = oldWeeks[0].split(/\|/);
            finishTime = getStartOfWeek(year, weekNo).time;
            startTime = finishTime - (3 * aWeekMillisecond);
            newWeeks = [...oldWeeks];
            for (let i = 1; i <= 3; i++) {
                const keyWeek = getKeyWeek(finishTime - (i * aWeekMillisecond));
                newWeeks = [keyWeek, ...newWeeks]
            }
            result = ({ operations, vessels, before_count }: any) => ({ operations, vessels, beforeCount: before_count });
        } else if (key === 'duplicate') {
            isLoad = afterCount;
            const [year, weekNo]: any = oldWeeks[oldWeeks.length - 1].split(/\|/);
            startTime = getStartOfWeek(year, weekNo).time + aWeekMillisecond;
            finishTime = duplicateFinishTime;
            newWeeks = [...oldWeeks];
            const diffWeek = Math.ceil((finishTime - startTime) / aWeekMillisecond);
            for (let i = 0; i < diffWeek; i++) {
                const keyWeek = getKeyWeek(startTime + (i * aWeekMillisecond));
                newWeeks = [...newWeeks, keyWeek];
            }
            result = ({ operations, vessels, after_count }: any) => ({ operations, vessels, afterCount: after_count });
        } else {
            isLoad = afterCount;
            const [year, weekNo] = oldWeeks[oldWeeks.length - 1].split(/\|/);
            startTime = getStartOfWeek(year, weekNo).time + aWeekMillisecond;
            finishTime = startTime + (3 * aWeekMillisecond) - 1;
            newWeeks = [...oldWeeks];
            for (let i = 0; i <= 2; i++) {
                const keyWeek = getKeyWeek(startTime + (i * aWeekMillisecond));
                newWeeks = [...newWeeks, keyWeek]
            }
            result = ({ operations, vessels, after_count }: any) => ({ operations, vessels, afterCount: after_count });
        }

        if (isLoad) {
            const response = result(yield call(OpService.getScheduleOfWeeks2, startTime, finishTime))
            const state = getStore();
            const { operations: oldOperations, level: oldLevel, vessels: oldVessels } = state.planOperation;
            const tenantId: string = yield select(selectTenantId);
            const { vessel: configVessel = initAutoPlanConfig.vessel } = yield select(selectOneSetting(PreferenceSetting.AUTO_PLAN));
            const vesselIds = yield select(selectOneSetting(PlanSetting.VESSEL_FILTER));

            const vessels = response.vessels.reduce((list: IVessel[], item: any) => {
                const index = list.findIndex(sub => sub.id === item.vessel.id);
                if (index !== -1) {
                    const temp = cloneObj(list);
                    temp[index].available_times = [...temp[index].available_times || [], ...item.vessel.available_times || []];
                    return temp;
                } else {
                    const { vessel } = item
                    const isOwn = `${tenantId}` === `${vessel.tenant_id}`;
                    const { priority } = configVessel[vessel.type] || {};

                    if (!isOwn) {
                        const { contracts } = vessel;
                        const contract = contracts.find((sub: IContract) => sub.tenant_id === tenantId
                            && sub.type === CONTRACT_TYPE.SINGLE
                            && sub.permission === CONTRACT_PERMISSION_TYPE.FULL)

                        if (contract)
                            vessel.permission = contract.id
                    }
                    const isShow = vesselIds.indexOf(vessel.id) !== -1;

                    list = [...list || [], { ...item.vessel, isShow, priority, index: list.length, isOwn }];
                }

                return list;
            }, oldVessels.map(vessel => ({
                ...vessel,
                available_times: [...vessel.available_times || []],
                contracts: [...vessel.contracts || []],
            })));

            const {
                operations,
                level,
                vessels: newVessels
            } = convertOpsFromResponse(response.operations, vessels, {
                operations: { ...oldOperations },
                level: oldLevel
            });

            yield put(planOpActions.lazyLoadingSuccess({
                result: { ...response, vessels: newVessels, operations, level },
                key,
                weeks: newWeeks,
            }))
        } else {
            yield put(planOpActions.updateWeeks(newWeeks))
        }
        action();
    } catch (error) {
        yield put(planOpActions.lazyLoadingFailure());
        action();
        console.log(error);
    }
}

function* getSitesWatcher({ ...args }): Generator<any, any, any> {
    const { abort } = args.payload;

    yield fork(function* () {
        try {
            const state: Partial<{
                sites: ISite[],
                harvests: any[],
            }> = {};

            const { sites = [], harvests = [] } = yield call(SiteService.getForPlan, abort);

            state.sites = sites;
            state.harvests = harvests;

            yield put(planOpActions.getSitesSuccess(state));
        } catch (error) {
            console.log(error);
            yield put(planOpActions.getSitesFailure());
        }
    });

    yield fork(function* () {
        try {

            const { request_tabs } = yield call(SiteService.serviceRequest, abort);
            yield put(planOpActions.setServiceRequests({ loadingServiceRequests: false, serviceRequests: request_tabs }))
        } catch (error) {
            yield put(planOpActions.setServiceRequests({ loadingServiceRequests: false }));
        }
    });
}

export interface IGetActivityLog {
    source: {
        operation: TOperation
        vessel: IVessel
        activity_log?: IOperation['activity_log']
    }[]
    properties: {
        [indexOp: string]: IProperty
    }
    signal?: AbortController
    showError?: boolean
    skipDurationEst?: boolean

    success?(ops: IOperations, options?: any): void

    failure?(error?: any): void
}

function* getActivityLogWatcher(args: PayloadAction<IGetActivityLog>): Generator<any, any, any> {
    try {
        const setting = yield select(selectSetting([PreferenceSetting.SITES_TIME, PreferenceSetting.FACTORIES_TIME, PreferenceSetting.AUTO_PLAN]));

        const opsNew: IOperations = {};

        const { source, properties, signal, skipDurationEst } = args.payload;
        let { start: startTime = Date.now() } = properties[source[0].operation.id] || {};
        let limitTime = Date.now();
        let extended: any = {}
        for (let index = 0, max = source.length; index < max; index++) {
            const item = source[index]
            const { id = index } = item.operation;
            const { operation_type } = item.operation;
            const property = { ...properties[id], start: startTime };
            const opts = { startTime, setting, property }
            const { data, param } = paramsActivityLog[operation_type](item, opts);

            const durationEst = skipDurationEst ? item.operation.duration_est : yield call(getActivityLogDurationEst, item, signal);

            let {
                activity_log,
                router_id,
                prev_op_router_id,
                support_vessels,
                round_map,
                tasks,
                duration,
                sub_operations,
            } = yield call(OpService.activityLog, param, signal);

            extended[id] = { support_vessels, tasks, round_map, duration, sub_operations }
            opsNew[id] = cloneObj(data);
            if (operation_type === OPERATION_TYPE.HARVEST || operation_type === OPERATION_TYPE.TREATMENT) {
                const resolved = resolveActivityLog(item.activity_log, activity_log, durationEst);
                activity_log = resolved.activity_log;
                opsNew[id].operation.duration_est = resolved.duration_est;
            }
            if (activity_log[0].est_start_time < limitTime) {
                const countDays = Math.ceil((limitTime - activity_log[0].est_start_time) / aDayMillisecond)
                activity_log = updateTime(activity_log, countDays * aDayMillisecond);
                startTime = datetime(startTime).add(countDays, 'day').time;
            } else {
                startTime = datetime(activity_log[activity_log.length - 1].est_start_time)
                    .add(1, 'day').startOf('day').time;
            }

            opsNew[id].activity_log = activity_log;
            opsNew[id].operation.router_id = router_id;
            opsNew[id].operation.prev_op_router_id = prev_op_router_id;

            if (round_map) {
                opsNew[id].operation.round_map = round_map;
            }
            if (sub_operations) {
                opsNew[id].operation.sub_operations = sub_operations;
            }
            if (tasks) {
                opsNew[id].operation.tasks = tasks;
            }

            limitTime = activity_log[activity_log.length - 1].est_start_time;
        }
        args.payload.success && args.payload.success(opsNew, extended);

    } catch (error: any) {
        const { showError = true } = args.payload;
        if (error.name !== 'AbortError' && showError) {
            showErrorResponse('Error', error);
        }
        console.log(error);
        args.payload.failure && args.payload.failure(error);
    }
}

function* getVesselsWatcher(args: PayloadAction<{ abort: AbortController, callback: any }>): Generator<any, any, any> {
    try {
        const { abort, callback } = args.payload;
        const vesselIds = yield select(selectOneSetting(PlanSetting.VESSEL_FILTER));

        const tenantId: string = yield select(selectTenantId);
        const weeks = yield select(selectPlan.weeks);
        const startTime = getMondayByKey(weeks[0]).time;
        const finishTime = getMondayByKey(weeks[weeks.length - 1]).time + aWeekMillisecond - 1;

        const plan = yield call(OpService.getScheduleOfWeeks2, startTime, finishTime, abort);
        const autoConfig = yield select(selectOneSetting(PreferenceSetting.AUTO_PLAN));


        const { vessel: configVessel = initAutoPlanConfig.vessel } = autoConfig;
        let isFlag = false;

        const vessels: IVessel[] = plan.vessels.map((item: any, index: number) => {
            const { vessel } = item
            const isOwn = `${tenantId}` === `${vessel.tenant_id}`;
            const { priority } = configVessel[vessel.type] || {};

            if (!isOwn) {
                const { contracts } = vessel;
                const contract = contracts.find((sub: IContract) => sub.tenant_id === tenantId
                    && sub.type === CONTRACT_TYPE.SINGLE
                    && sub.permission === CONTRACT_PERMISSION_TYPE.FULL)

                if (contract)
                    vessel.permission = contract.id
            }
            const isShow = vesselIds.indexOf(vessel.id) !== -1;
            if (isShow)
                isFlag = true;
            return { ...vessel, isShow, priority, index, isOwn };
        });

        const { operations, level, vessels: newVessels } = convertOpsFromResponse(plan.operations, vessels);

        yield put(planOpActions.getVesselsSuccess({
            plan: { ...plan, operations, level, vessels: isFlag ? vessels : newVessels.map(item => ({ ...item, isShow: true })) }
        }));

        if (callback) {
            const { vessels } = plan;
            callback(vessels);
        }

        const canceled: IOperation[] = yield select(selectPlan.opsCanceled);
        if (canceled.length > 0) {
            yield put(planOpActions.getCancelOps(canceled));
        }
    } catch (error) {
        console.log(error);
        yield put(planOpActions.getVesselsFailure());
    }
}

function* renewWatcher(args: ReturnType<typeof planOpActions.renewOp>): Generator<any, any, any> {
    try {
        const data = args.payload;
        const { id } = data.operation;
        const state = getStore();
        const { canceled, operations, level } = state.planOperation;
        const factories = selectFactory.data(state);

        const ops = {
            ...operations,
            [id]: {
                ...data,
                ...checkValidOperation({
                    operations,
                    data,
                    level,
                    factories,
                }),
                operation: { ...data.operation, status: OP_STATUS.NEW },
                action_type: OPERATION_CONFIRM_STATUS.UPDATE
            }
        }
        yield put(planOpActions.updateState({
            canceled: canceled.map(item => item.operation.id === id ? { ...item, isShow: false } : item),
            opFocusId: id,
            operations: ops,
            level: upLevel(level, data, ops)
        }))
        setTimeout(() => {
            const el = getElById(id);
            if (el)
                el.scrollIntoView(VIEW_SMOOTH)
        }, 300)
    } catch (error) {
        console.log(error);
    }
}

const updateInvoicesAfterVesselChange = (target: IOperation, operations: IOperations) => {
    const previous = operations[target.operation.id];
    if (!previous || previous.vessel.id === target.vessel.id)
        return { target, operations };

    const movedId = target.operation.id;
    const isEvent = target.operation.operation_type === OPERATION_TYPE.EVENT;
    const relatedIds = new Set((previous.operation.invoices || []).map(invoice => invoice.operation_id));
    const updatedOperations = Object.keys(operations).reduce<IOperations>((result, id) => {
        const item = operations[id];
        const invoices = item.operation.invoices || [];
        const shouldClear = !isEvent
            && (relatedIds.has(id) || invoices.some(invoice => invoice.operation_id === movedId));

        if (shouldClear) {
            result[id] = {
                ...item,
                operation: { ...item.operation, invoices: [] },
                action_type: item.action_type || OPERATION_CONFIRM_STATUS.UPDATE,
            };
            return result;
        }

        if (isEvent && id !== movedId && invoices.some(invoice => invoice.operation_id === movedId)) {
            const remainingInvoices = invoices.filter(invoice => invoice.operation_id !== movedId);
            const hasRelatedOperation = remainingInvoices.some(invoice => invoice.operation_id !== item.operation.id);
            result[id] = {
                ...item,
                operation: {
                    ...item.operation,
                    invoices: hasRelatedOperation ? remainingInvoices : [],
                },
                action_type: item.action_type || OPERATION_CONFIRM_STATUS.UPDATE,
            };
        }

        return result;
    }, { ...operations });
    const updatedTarget = {
        ...target,
        operation: { ...target.operation, invoices: [] },
    };

    return {
        target: updatedTarget,
        operations: { ...updatedOperations, [movedId]: updatedTarget },
    };
};

function* dragOpWatcher(args: ReturnType<typeof planOpActions.dragOperation>): Generator<any, any, any> {
    try {
        let target = args.payload
        const state = getStore();
        const { operations: dataOld } = state.planOperation;
        let { level } = state.planOperation;
        const { id } = target.operation;

        if (!level[dataOld[id]?.vessel.id])
            return null;

        const { [dataOld[id].vessel.id]: levelOld } = level;
        level = downLevel(level, dataOld[id], dataOld);
        const invoiceUpdate = updateInvoicesAfterVesselChange(target, dataOld);
        target = invoiceUpdate.target;
        const dataCurrent = invoiceUpdate.operations;
        const list = !target.operation.parallel ? pushOperations(target, {
            ...dataCurrent,
            [id]: target
        }, false) : { [id]: target };
        const factories = selectFactory.data(state);

        const operations = Object.keys(list).reduce((operations: IOperations, key: string) => {
            const { status } = operations[key];
            const action_type = dataCurrent[key].action_type || OPERATION_CONFIRM_STATUS.UPDATE;

            if (key.toString() === target.operation.id.toString() || status === VALIDATION_STATUS.ERROR) {
                const { contract_id, available_time_id } = getContract(operations[key]);
                operations = { ...operations, [key]: { ...operations[key], operation: { ...operations[key].operation, contract_id, available_time_id } } }
                operations = {
                    ...operations,
                    [key]: {
                        ...operations[key],
                        ...checkValidOperation({
                            operations,
                            level,
                            data: operations[key],
                            factories,
                        }),
                        action_type
                    }
                }
            }

            level = upLevel(level, operations[id], operations, levelOld);
            return operations;
        }, { ...dataCurrent, ...list });
        yield put(planOpActions.updateState({
            opFocusId: id,
            operations,
            level
        }))
    } catch (error) {
        console.log(error);
    }
}

function* updateOpsWatcher(args: ReturnType<typeof planOpActions.updateOps>): Generator<any, any, any> {
    try {
        const { add = {}, update = {}, files: newFiles = {}, isCheck = true, isFocus = '', deleteIds = {}, blockIds } = args.payload;
        const validation = isCheck ? (props: ICheckValidOperation) => checkValidOperation(props) : () => ({});
        const state = getStore();
        let { indexId, fishChanges, operations, level, files, operationsDelete } = state.planOperation;
        const source = { ...add, ...update };
        const firstKey = Object.keys(source)[0];
        const { parallel } = source[firstKey].operation;
        let moveOp = {};

        for (const mainId of Object.keys(deleteIds)) {
            const ids = deleteIds[mainId];
            const partDeleteOps: IOperation[] = [];

            for (const id of ids) {
                const item = operations[id];
                if (!item) continue;

                level = downLevel(level, item, operations);
                const { [id]: _, ...restOps } = operations;
                operations = restOps;
                if (item.operation.status !== OP_STATUS.NEW)
                    partDeleteOps.push(item);
            }

            if (partDeleteOps.length) {
                operationsDelete = {
                    ...operationsDelete,
                    [mainId]: [...(operationsDelete[mainId] || []), ...partDeleteOps],
                };
            }
        }

        // Delete operations
        if (!parallel) {
            const lastKey = Object.keys(source)[Object.keys(source).length - 1];
            const start = source[firstKey].activity_log[0].est_start_time;
            const finish = source[lastKey].activity_log[source[lastKey].activity_log.length - 1].est_start_time;
            const { id: vesselId } = source[firstKey].vessel;
            const { id, tenant_id = '' } = source[firstKey].operation;
            moveOp = updateNextOperation({
                level,
                startTime: start,
                finishTime: finish,
                tenantId: tenant_id,
                vesselId,
                ids: blockIds || [id],
                operations
            });
        }

        operations = { ...operations, ...moveOp };

        for (const key of Object.keys(add)) {
            const item = add[key];
            const { contract_id, available_time_id } = getContract(item);
            const data = {
                ...item,
                operation: {
                    ...item.operation,
                    operation_code: `${indexId}`,
                    contract_id,
                    available_time_id,
                    status: OP_STATUS.NEW
                },
            };

            operations = {
                ...operations,
                [key]: {
                    ...data,
                    ...checkValidOperation({ operations, level, data, }),
                    action_type: OPERATION_CONFIRM_STATUS.NEW,
                }
            };

            fishChanges = addFishChanges(fishChanges, operations[key]);

            if (key === isFocus)
                focusOp(data.operation.id);

            indexId++;
            level = upLevel(level, data, operations);
        }

        const realUpdate: typeof operations = { ...moveOp, ...update };
        for (const key of Object.keys(realUpdate)) {
            const currentOp = operations[key];
            if (!currentOp) continue;

            level = downLevel(level, currentOp, operations);
            const { contract_id, available_time_id } = getContract(realUpdate[key]);

            const item = {
                ...realUpdate[key],
                operation: { ...realUpdate[key].operation, contract_id, available_time_id, },
            };

            const oldOperation = cloneObj(currentOp.operation);

            operations = {
                ...operations,
                [key]: {
                    ...item,
                    ...validation({ operations, level, data: item, }),
                    action_type: item.action_type || currentOp.action_type || OPERATION_CONFIRM_STATUS.UPDATE,
                }
            };

            fishChanges = addFishChanges(fishChanges, operations[key], oldOperation);

            if (key === isFocus)
                focusOp(key);

            level = upLevel(level, item, operations);
        }

        yield put(planOpActions.updateState({
            indexId,
            fishChanges,
            operations,
            level,
            operationsDelete,
            files: { ...files, ...newFiles }
        }))
    } catch (error) {
        console.log(error);
    }
}

function* updateOpsByWsWatcher(args: ReturnType<typeof planOpActions.updateOpsByWs>): Generator<any, any, any> {
    try {
        const { data, mode } = args.payload;
        const state = getStore();
        let { operations, vessels, canceled, level } = state.planOperation;
        const update: Partial<typeof state.planOperation> = {};
        let newOperations;
        if (mode === 'remove') {
            data.forEach((key: string) => {
                const item = operations[key];
                if (!item)
                    return;
                level = downLevel(level, item, operations);
                const { [key]: old, ...value } = operations;
                operations = { ...value };
            })
            newOperations = operations;
            update.level = level;
        } else if (mode === 'update-without-level') {
            newOperations = data.reduce((list: typeof operations, item: IOperation) => {
                const { id, vessel_id } = item.operation;
                const index = vessels.findIndex(vessel => vessel.id === vessel_id);
                return { ...list, [id]: cloneObj({ ...item, vessel: { ...vessels[index], index } }) };
            }, operations);
        } else if (mode === 'update') {
            newOperations = data.reduce((list: any, item: IOperation) => {
                const { id } = item.operation;
                const index = vessels.findIndex(vessel => vessel.id === item.operation.vessel_id)
                list = { ...list, [id]: cloneObj({ ...item, vessel: { ...vessels[index], index } }) };
                level = downLevel(level, list[id], list);
                level = upLevel(level, list[id], list);
                return list;
            }, operations);
            update.level = level;
        } else {
            newOperations = data.reduce((list: typeof operations, item) => {
                const { id, vessel_id, status } = item.operation;
                const index = vessels.findIndex(vessel => vessel.id === vessel_id);
                const operation = { ...item, vessel: { ...vessels[index], index } };
                level = downLevel(level, operation, list);
                if (status === OP_STATUS.CANCELED) {
                    const indexOp = canceled.findIndex((sub: IOperation) => sub.operation.id === id);
                    if (indexOp === -1)
                        canceled = [...canceled, operation];
                    else {
                        const temp = cloneObj(canceled);
                        temp[indexOp] = { ...operation, isShow: true }
                        canceled = temp;
                    }
                    update.canceled = canceled;
                    const { [id]: old, ...value } = list;
                    return value || {};
                }
                list = { ...list, [id]: operation };
                level = upLevel(level, list[id], list);
                return list;
            }, operations);
            update.level = level;
        }
        update.operations = newOperations;
        yield put(planOpActions.updateState(update))
    } catch (error) {
        console.log(error);
    }
}


function* updateOpByWsWatcher(args: ReturnType<typeof planOpActions.updateOpsByWs>): Generator<any, any, any> {
    try {
        const { data } = args.payload;
        const state = getStore();
        let { operations, vessels, level } = state.planOperation;
        const newOperations = data.reduce((list: any, item: IOperation) => {
            const { id } = item.operation;
            const index = vessels.findIndex(vessel => vessel.id === item.operation.vessel_id)
            list = { ...list, [id]: cloneObj({ ...item, vessel: { ...vessels[index], index } }) };
            level = downLevel(level, list[id], list);
            level = upLevel(level, list[id], list);
            return list;
        }, operations);

        yield put(planOpActions.updateState({
            operations: newOperations,
            level,
        }))
    } catch (error) {
        console.log(error);
    }
}


function* autoPlanWatcher(args: ReturnType<typeof planOpActions.autoPlan>): Generator<any, any, any> {
    try {
        const { operations, harvests } = args.payload;
        const state = getStore();
        const { harvests: rootHarvests, operations: operationsOld, vessels } = state.planOperation;
        let { level } = state.planOperation;
        const updatedHarvests = rootHarvests.map((item: any) => {
            const updatedHarvest = harvests[item.harvest_id];
            if (!updatedHarvest)
                return item;

            const { fish_amount } = updatedHarvest;
            return {
                ...item,
                fish_amount,
                total_weight: fish_amount * item.avg_weight
            };
        });
        const operationsNew = operations.reduce((list: IOperations, item) => {
            const { vessel_id, id } = item.operation;
            const index = vessels.findIndex((vesselItem: any) => vesselItem.id === vessel_id);
            list = {
                ...list,
                [id]: {
                    ...item as IOperation,
                    action_type: OPERATION_CONFIRM_STATUS.NEW,
                    vessel: { ...vessels[index], index }
                }
            };
            level = upLevel(level, list[id], list);
            return list;
        }, operationsOld);
        yield put(planOpActions.updateState({
            harvests: updatedHarvests,
            operations: operationsNew,
            level
        }))
    } catch (error) {
        console.log(error);
    }
}

function* deleteOpsWatcher(args: ReturnType<typeof planOpActions.deleteOps>): Generator<any, any, any> {
    try {
        const ids = args.payload;
        const state = getStore();
        let { canceled: newCanceled, operations, fishChanges, level } = state.planOperation;

        const operationsNew = ids.reduce((list: IOperations, key: string) => {
            const { [key]: item = null, ...args } = list;
            const currentOp = cloneObj(item);
            if (currentOp) {
                fishChanges = removeFishChanges(fishChanges, currentOp);
                level = downLevel(level, currentOp, operations);
            }
            newCanceled = newCanceled.filter(sub => sub.operation.id !== key);
            return args;
        }, operations || {});

        yield put(planOpActions.updateState({
            canceled: newCanceled,
            fishChanges,
            operations: operationsNew,
            level
        }))
    } catch (error) {
        console.log(error);
    }
}

function* clearOpsWatcher(args: ReturnType<typeof planOpActions.clearOps>): Generator<any, any, any> {
    try {
        const state = getStore();
        let { operations, fishChanges, level, vessels } = state.planOperation;

        const { listOfDelete, listOfUpdate } = args.payload;
        let operationsNew = listOfDelete.reduce((list: IOperations, key: string) => {
            const { [key]: item, ...args } = list;
            list = args;
            fishChanges = removeFishChanges(fishChanges, item);
            level = downLevel(level, item, operations);
            return list;
        }, operations)

        operationsNew = listOfUpdate.reduce((rs: IOperations, item) => {
            const { id, vessel_id } = item.operation;
            const vessel = vessels.find(sub => sub.id === vessel_id);
            level = downLevel(level, operations[id], operations);
            if (vessel) {
                rs = { ...rs, [id]: { ...item, vessel } };
                fishChanges = removeFishChanges(fishChanges, item);
                level = upLevel(level, rs[id], operations);
            }
            return rs;
        }, operationsNew);

        yield put(planOpActions.updateState({
            operations: operationsNew,
            fishChanges,
            level
        }))
    } catch (error) {
        console.log(error);
    }
}

function* mergeOpWatcher(args: ReturnType<typeof planOpActions.mergeOp>): Generator<any, any, any> {
    try {
        const state = getStore();
        let { operations, level } = state.planOperation;
        let operationsDelete = cloneObj(state.planOperation.operationsDelete) || {};

        const { data, deletedId } = args.payload;
        const { action_type } = data;
        const { id } = data.operation;
        const { [deletedId]: opDelete, ...newOps } = operations;
        level = downLevel(level, opDelete, operations);

        if (opDelete.action_type !== OPERATION_CONFIRM_STATUS.NEW) {
            operationsDelete[id] = [...operationsDelete[id] || [], opDelete];
        }

        if (operationsDelete[deletedId]) {
            operationsDelete[id] = [...operationsDelete[id] || [], ...operationsDelete[deletedId]]
            const { [deletedId]: _, ...args } = operationsDelete;
            operationsDelete = args;
        }
        const factories = selectFactory.data(getStore());

        yield put(planOpActions.updateState({
            operationsDelete,
            operations: {
                ...newOps,
                [id]: {
                    ...data,
                    ...checkValidOperation({
                        operations: newOps,
                        data,
                        level,
                        factories,
                    }),
                    action_type: action_type || OPERATION_CONFIRM_STATUS.UPDATE
                }
            },
            level
        }))
    } catch (error) {
        console.log(error);
    }
}

function* cancelOpWatcher(action: ReturnType<typeof planOpActions.cancelOp>): SagaIterator {
    try {
        let level = yield select(selectPlan.level);
        const canceled = yield select(selectPlan.canceled);
        const operations = yield select(selectPlan.ops);
        const id = action.payload;
        const { [id]: data, ...args } = operations;
        level = downLevel(level, data, operations);

        yield put(planOpActions.updateState({
            operations: args,
            canceled: [...canceled, { ...data, operation: { ...data.operation, status: OP_STATUS.CANCELED } }],
            level
        }))
    } catch (error) {
        console.log(error);
    }
}

function* deleteCancelOpWatcher(args: ReturnType<typeof planOpActions.deleteCancelOp>): Generator<any, any, any> {
    try {
        const state = getStore();
        let { canceled, operations, level } = state.planOperation;

        const id = args.payload;
        const { [id]: op, ...newOps } = operations;
        level = downLevel(level, op, operations);

        yield put(planOpActions.updateState({
            canceled: canceled.map(item => item.operation.id === id ? { ...item, isShow: true } : item),
            operations: newOps,
            level
        }))
    } catch (error) {
        console.log(error);
    }
}


function* togglePopupOperationWatcher(args: ReturnType<typeof planOpActions.togglePopupOperation>): Generator<any, any, any> {
    try {
        let { vessels, operations, level } = yield select(selectPlan.root);
        let popupOperation;
        const { e, defaultOpType, location, operation, callback, useDroppedStartTime = false, checkMiddle = () => true } = args.payload;

        const userTenantId = yield select(selectTenantId);
        if (e) {
            const target = onMouseDown(e, vessels, useDroppedStartTime);
            if (!target)
                return;

            if (!checkMiddle(target))
                return;

            const { vessel, operationId, availableTimeId, contractId, startTime } = target;
            let params: any;
            if (operationId) {
                params = funcHarvest[FuncOp.BeginMerge]({ id: operationId, operations });
            } else if (vessel) {
                const finishTime = startTime + aDayMillisecond - 1;
                const opMode = selectOpMode(getStore());
                const finish = Object.keys(level[vessel.id] || {}).reduce((rs, key) => {
                    const { activity_log } = operations[key];

                    const value = getTimeByOpMode[opMode](activity_log[activity_log.length - 1]);
                    if (startTime <= value && activity_log[0].est_start_time <= finishTime)
                        if (rs < value)
                            return value;
                    return rs;
                }, 0);
                params = funcGlobal[FuncOp.BeginCreate]({
                    vessel,
                    startTime: finish ? finish + TIME_PER_STEP : startTime,
                    availableTimeId,
                    contractId,
                    defaultOpType,
                    userTenantId,
                    operation
                })
            }
            popupOperation = { ...params, location, action: callback }
        } else {
            popupOperation = args.payload
        }

        yield put(planOpActions.updateState({
            popupOperation
        }))
    } catch (error) {
        console.log(error);
    }
}

export default function* planOperationSaga() {
    yield all([
        takeEvery(planOpActions.initialRequest.type, initialWatcher),
        takeEvery(planOpActions.confirmOpRequest.type, confirmOperationsWatcher),
        takeEvery(planOpActions.lazyLoadingRequest.type, lazyLoadingWatcher),
        takeEvery(planOpActions.getSitesRequest.type, getSitesWatcher),
        takeEvery(planOpActions.getActivityLog.type, getActivityLogWatcher),
        takeEvery(planOpActions.getVesselsRequest.type, getVesselsWatcher),
        takeEvery(planOpActions.renewOp.type, renewWatcher),
        takeEvery(planOpActions.dragOperation.type, dragOpWatcher),
        takeEvery(planOpActions.updateOps.type, updateOpsWatcher),
        takeEvery(planOpActions.updateOpByWs.type, updateOpByWsWatcher),
        takeEvery(planOpActions.updateOpsByWs.type, updateOpsByWsWatcher),
        takeEvery(planOpActions.autoPlan.type, autoPlanWatcher),
        takeEvery(planOpActions.deleteOps.type, deleteOpsWatcher),
        takeEvery(planOpActions.clearOps.type, clearOpsWatcher),
        takeEvery(planOpActions.mergeOp.type, mergeOpWatcher),
        takeEvery(planOpActions.deleteCancelOp.type, deleteCancelOpWatcher),
        takeEvery(planOpActions.togglePopupOperation.type, togglePopupOperationWatcher),
        takeEvery(planOpActions.cancelOp.type, cancelOpWatcher),
    ])
}
